import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Pencil, Trash2, Search, X, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AppContext";
import SEO from "@/components/SEO";
import ImageUploader, { SingleImageField } from "@/components/ImageUploader";
import { CategoryBannersSection, CertificationsSection, BrochureSection, AlertsSection, FounderSection } from "@/components/AdminSections";

const TABS = ["Overview", "Inquiries", "Products", "Business Info", "Website Content", "SEO"];

const inputCls = "w-full border border-forest/15 bg-white px-3.5 py-2.5 text-sm text-forest focus:border-gold focus:outline-none";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.2em] text-forest/55 mb-1.5";

const EMPTY_PRODUCT = {
  name: "", category: "rice", slug: "", short_description: "", description: "",
  images: [], forms: "", packaging: "", certifications: "",
  origin: "India", grade: "", moq: "", supply_capacity: "", shipping_info: "",
  specs: "", status: "active", featured: false, seo_title: "", seo_description: "",
};

function productToForm(p) {
  return {
    name: p.name, category: p.category, slug: p.slug,
    short_description: p.short_description, description: p.description,
    images: p.images || [], forms: (p.forms || []).join(", "),
    packaging: (p.packaging || []).join(", "), certifications: (p.certifications || []).join(", "),
    origin: p.origin, grade: p.grade, moq: p.moq, supply_capacity: p.supply_capacity,
    shipping_info: p.shipping_info,
    specs: (p.specs || []).map((s) => `${s.label}: ${s.value}`).join("\n"),
    status: p.status, featured: !!p.featured,
    seo_title: p.seo_title || "", seo_description: p.seo_description || "",
  };
}

function formToPayload(f) {
  return {
    name: f.name, category: f.category, slug: f.slug,
    short_description: f.short_description, description: f.description,
    images: f.images.map((s) => s.trim()).filter(Boolean),
    forms: f.forms.split(",").map((s) => s.trim()).filter(Boolean),
    packaging: f.packaging.split(",").map((s) => s.trim()).filter(Boolean),
    certifications: f.certifications.split(",").map((s) => s.trim()).filter(Boolean),
    origin: f.origin, grade: f.grade, moq: f.moq, supply_capacity: f.supply_capacity,
    shipping_info: f.shipping_info,
    specs: f.specs.split("\n").map((l) => {
      const idx = l.indexOf(":");
      if (idx === -1) return null;
      return { label: l.slice(0, idx).trim(), value: l.slice(idx + 1).trim() };
    }).filter((s) => s && s.label),
    status: f.status, featured: f.featured,
    seo_title: f.seo_title, seo_description: f.seo_description,
  };
}

const STATUS_STYLES = {
  new: "bg-gold/20 text-gold-dark",
  contacted: "bg-forest/10 text-forest",
  quoted: "bg-forest text-parchment",
  closed: "bg-forest/5 text-forest/50",
};

export default function Admin() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [overview, setOverview] = useState(null);
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [inqSearch, setInqSearch] = useState("");
  const [inqType, setInqType] = useState("");
  const [inqStatus, setInqStatus] = useState("");
  const [expandedInq, setExpandedInq] = useState(null);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!checking && !user) navigate("/admin/login");
  }, [checking, user, navigate]);

  const loadOverview = () => api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
  const loadSettings = () => api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  const loadProducts = () => api.get("/admin/products").then((r) => setProducts(r.data.products || [])).catch(() => {});
  const loadInquiries = () => {
    const params = new URLSearchParams();
    if (inqSearch) params.set("search", inqSearch);
    if (inqType) params.set("type", inqType);
    if (inqStatus) params.set("status", inqStatus);
    api.get(`/admin/inquiries?${params.toString()}`).then((r) => setInquiries(r.data.inquiries || [])).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    loadOverview();
    loadSettings();
    loadProducts();
  }, [user]);

  useEffect(() => {
    if (user) loadInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, inqType, inqStatus]);

  if (checking || !user) {
    return <div className="flex min-h-screen items-center justify-center bg-parchment"><Loader2 className="h-8 w-8 animate-spin text-gold-dark" /></div>;
  }

  const saveSettings = async (keys) => {
    setSaving(true);
    try {
      const body = {};
      keys.forEach((k) => { body[k] = settings[k]; });
      const { data } = await api.put("/admin/settings", body);
      setSettings(data);
      toast.success("Settings saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveProduct = async () => {
    setSaving(true);
    try {
      const payload = formToPayload(editor.form);
      if (editor.id) {
        await api.put(`/admin/products/${editor.id}`, payload);
        toast.success("Product updated.");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created.");
      }
      setEditor(null);
      loadProducts();
      loadOverview();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted.");
      loadProducts();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setInquiryStatus = async (inq, status) => {
    try {
      await api.put(`/admin/inquiries/${inq.id}`, { status });
      setInquiries((list) => list.map((i) => (i.id === inq.id ? { ...i, status } : i)));
      toast.success(`Marked as ${status}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setS = (path, value) => {
    setSettings((s) => {
      const copy = JSON.parse(JSON.stringify(s));
      const keys = path.split(".");
      let ref = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        if (ref[keys[i]] == null) ref[keys[i]] = {};
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  const overviewCards = overview ? [
    ["Total Products", overview.products, "products"],
    ["Active Products", overview.products_active, "products-active"],
    ["Total Inquiries", overview.inquiries_total, "inquiries-total"],
    ["New Inquiries", overview.inquiries_new, "inquiries-new"],
    ["Quote Requests", overview.quotes, "quotes"],
    ["Contact Messages", overview.contacts, "contacts"],
  ] : [];

  return (
    <div className="min-h-screen bg-parchment" data-testid="admin-dashboard">
      <SEO title="Admin | AnVibes Exports" path="/admin" />
      <div className="bg-forest-deep border-b border-white/10">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 sm:px-8 py-4">
          <div>
            <p className="font-serif text-xl text-parchment">AnVibes<span className="text-gold text-xs align-super">™</span> Admin</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-parchment/50">{user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-parchment/70 hover:text-gold-light transition-colors">
              View Site <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-button" className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-parchment/80 hover:border-gold hover:text-gold-light transition-colors">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 flex gap-1 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setTab(t)}
              className={`whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors border-b-2 ${
                tab === t ? "border-gold text-gold-light" : "border-transparent text-parchment/55 hover:text-parchment"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 py-10">
        {tab === "Overview" && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-overview">
            {overviewCards.map(([label, value, key]) => (
              <div key={key} className="border border-forest/10 bg-white p-7" data-testid={`admin-stat-${key}`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-forest/50">{label}</p>
                <p className="mt-2 font-serif text-4xl text-forest">{value}</p>
              </div>
            ))}
            <div className="border border-gold/40 bg-gold/10 p-7">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-dark">Quick Action</p>
              <button onClick={() => setTab("Inquiries")} data-testid="admin-goto-inquiries" className="mt-2 font-serif text-2xl text-forest underline decoration-gold underline-offset-4 hover:text-gold-dark transition-colors">
                Review new inquiries
              </button>
            </div>
          </div>
        )}

        {tab === "Inquiries" && (
          <div data-testid="admin-inquiries">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-forest/40" />
                <input
                  data-testid="admin-inquiry-search"
                  value={inqSearch}
                  onChange={(e) => setInqSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadInquiries()}
                  placeholder="Search name, email, company, product..."
                  className="w-full border border-forest/15 bg-white py-2.5 pl-9 pr-3 text-sm focus:border-gold focus:outline-none"
                />
              </div>
              <select data-testid="admin-inquiry-type-filter" value={inqType} onChange={(e) => setInqType(e.target.value)} className="border border-forest/15 bg-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none">
                <option value="">All types</option><option value="quote">Quote requests</option><option value="contact">Contact messages</option>
              </select>
              <select data-testid="admin-inquiry-status-filter" value={inqStatus} onChange={(e) => setInqStatus(e.target.value)} className="border border-forest/15 bg-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none">
                <option value="">All statuses</option><option value="new">New</option><option value="contacted">Contacted</option><option value="quoted">Quoted</option><option value="closed">Closed</option>
              </select>
              <button onClick={loadInquiries} data-testid="admin-inquiry-search-button" className="bg-forest px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-parchment hover:bg-forest-leaf transition-colors">Search</button>
            </div>

            <div className="mt-6 space-y-3">
              {inquiries.length === 0 && <p className="border border-forest/10 bg-white p-10 text-center text-sm text-forest/50" data-testid="admin-inquiries-empty">No inquiries found.</p>}
              {inquiries.map((inq) => (
                <div key={inq.id} className="border border-forest/10 bg-white" data-testid={`admin-inquiry-${inq.id}`}>
                  <button onClick={() => setExpandedInq(expandedInq === inq.id ? null : inq.id)} className="flex w-full flex-wrap items-center gap-4 px-5 py-4 text-left">
                    <span className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.14em] ${STATUS_STYLES[inq.status] || ""}`}>{inq.status}</span>
                    <span className="font-serif text-lg text-forest">{inq.name}</span>
                    <span className="text-xs text-forest/50">{inq.company}</span>
                    <span className="text-xs text-forest/50">{inq.type === "quote" ? `Quote · ${inq.product || inq.product_category || "General"}` : "Contact"}</span>
                    <span className="ml-auto flex items-center gap-2">
                      {inq.whatsapp_alert && (
                        <span title={inq.whatsapp_alert} data-testid={`inquiry-wa-alert-${inq.id}`} className={`px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] ${inq.whatsapp_alert === "sent" ? "bg-forest/10 text-forest" : "bg-terracotta/15 text-terracotta"}`}>
                          WA {inq.whatsapp_alert === "sent" ? "sent" : "failed"}
                        </span>
                      )}
                      <span className="font-mono text-[10px] text-forest/40">{new Date(inq.created_at).toLocaleDateString()}</span>
                    </span>
                  </button>
                  {expandedInq === inq.id && (
                    <div className="border-t border-forest/10 px-5 py-5 grid gap-x-8 gap-y-2.5 sm:grid-cols-2 text-sm">
                      {[["Email", inq.email], ["Phone", inq.phone], ["WhatsApp", inq.whatsapp], ["Country", inq.country],
                        ["Category", inq.product_category], ["Product", inq.product], ["Quantity", inq.quantity],
                        ["Packaging", inq.packaging], ["Destination Port", inq.destination_port], ["Target Date", inq.target_date],
                        ["Product Interest", inq.product_interest]].filter(([, v]) => v).map(([k, v]) => (
                        <p key={k}><span className="font-mono text-[10px] uppercase tracking-[0.16em] text-forest/45 block">{k}</span><span className="text-forest">{v}</span></p>
                      ))}
                      {inq.message && <p className="sm:col-span-2"><span className="font-mono text-[10px] uppercase tracking-[0.16em] text-forest/45 block">Message</span><span className="text-forest whitespace-pre-wrap">{inq.message}</span></p>}
                      <div className="sm:col-span-2 mt-2 flex flex-wrap gap-2">
                        {["new", "contacted", "quoted", "closed"].map((s) => (
                          <button key={s} onClick={() => setInquiryStatus(inq, s)} data-testid={`inquiry-status-${s}-${inq.id}`} className={`px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors ${inq.status === s ? "bg-forest text-parchment" : "border border-forest/20 text-forest hover:border-gold"}`}>
                            {s}
                          </button>
                        ))}
                        <a href={`mailto:${inq.email}`} data-testid={`inquiry-reply-${inq.id}`} className="px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] bg-gold/20 text-gold-dark hover:bg-gold/30 transition-colors">
                          Reply by Email
                        </a>
                        {(inq.whatsapp || inq.phone) && (
                          <a href={`https://wa.me/${(inq.whatsapp || inq.phone).replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hello ${inq.name}, thank you for your inquiry to AnVibes Exports${inq.product ? ` regarding ${inq.product}` : ""}.`)}`} target="_blank" rel="noopener noreferrer" data-testid={`inquiry-whatsapp-${inq.id}`} className="px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] bg-forest-leaf text-parchment hover:bg-forest transition-colors">
                            Reply on WhatsApp
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Products" && (
          <div data-testid="admin-products">
            <div className="flex items-center justify-between">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-forest/50">{products.length} products</p>
              <button onClick={() => setEditor({ id: null, form: { ...EMPTY_PRODUCT } })} data-testid="admin-add-product-button" className="inline-flex items-center gap-2 bg-forest px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-parchment hover:bg-forest-leaf transition-colors">
                <Plus className="h-4 w-4" /> Add Product
              </button>
            </div>
            <div className="mt-6 overflow-x-auto border border-forest/10 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-forest/10 text-left">
                    <th className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.16em] text-forest/50">Product</th>
                    <th className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.16em] text-forest/50">Category</th>
                    <th className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.16em] text-forest/50">Status</th>
                    <th className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.16em] text-forest/50">Featured</th>
                    <th className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.16em] text-forest/50 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-forest/5 hover:bg-parchment/60" data-testid={`admin-product-row-${p.slug}`}>
                      <td className="px-5 py-3.5 font-medium text-forest">{p.name}<span className="block font-mono text-[10px] text-forest/40">/{p.slug}</span></td>
                      <td className="px-5 py-3.5 capitalize text-forest/70">{p.category}</td>
                      <td className="px-5 py-3.5"><span className={`px-2 py-0.5 text-[10px] font-mono uppercase ${p.status === "active" ? "bg-forest/10 text-forest" : "bg-terracotta/15 text-terracotta"}`}>{p.status}</span></td>
                      <td className="px-5 py-3.5 text-forest/70">{p.featured ? "Yes" : "—"}</td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={() => setEditor({ id: p.id, form: productToForm(p) })} data-testid={`admin-edit-product-${p.slug}`} className="p-2 text-forest/60 hover:text-gold-dark transition-colors" aria-label={`Edit ${p.name}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => deleteProduct(p)} data-testid={`admin-delete-product-${p.slug}`} className="p-2 text-forest/60 hover:text-terracotta transition-colors" aria-label={`Delete ${p.name}`}><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "Business Info" && settings && (
          <div className="max-w-3xl space-y-6" data-testid="admin-business-info">
            <div className="border border-gold/40 bg-gold/10 p-5" data-testid="admin-invocation-section">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="font-serif text-xl text-forest">Top Invocation Strip</h3>
                  <p className="mt-0.5 text-xs text-forest/55">Shown above the navigation on every page.</p>
                </div>
                <label className="flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-forest cursor-pointer">
                  <input type="checkbox" checked={settings.invocation?.enabled !== false} onChange={(e) => setS("invocation.enabled", e.target.checked)} data-testid="admin-field-invocation-enabled" className="h-4 w-4 accent-[#C69A43]" />
                  Visible
                </label>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Devanagari Text</label><input className={inputCls} data-testid="admin-field-invocation-devanagari" value={settings.invocation?.devanagari || ""} onChange={(e) => setS("invocation.devanagari", e.target.value)} placeholder="श्री मात्रे नमः" /></div>
                <div><label className={labelCls}>English Text</label><input className={inputCls} data-testid="admin-field-invocation-english" value={settings.invocation?.english || ""} onChange={(e) => setS("invocation.english", e.target.value)} placeholder="Shri Maatre Namah" /></div>
              </div>
              <button onClick={() => saveSettings(["invocation"])} disabled={saving} data-testid="admin-save-invocation" className="mt-4 bg-forest px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest-leaf transition-colors disabled:opacity-60">Save Invocation</button>
            </div>
            <AlertsSection settings={settings} setS={setS} saveSettings={saveSettings} saving={saving} />
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className={labelCls}>Brand (with ™)</label><input className={inputCls} data-testid="admin-field-brand" value={settings.business.brand} onChange={(e) => setS("business.brand", e.target.value)} /></div>
              <div><label className={labelCls}>Public Business Name</label><input className={inputCls} data-testid="admin-field-public-name" value={settings.business.public_name} onChange={(e) => setS("business.public_name", e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Legal Company Name</label><input className={inputCls} data-testid="admin-field-legal-name" value={settings.business.legal_name} onChange={(e) => setS("business.legal_name", e.target.value)} /></div>
              <div><label className={labelCls}>Trade Name (shown in Business Information)</label><input className={inputCls} data-testid="admin-field-trade-name" value={settings.business.trade_name || ""} onChange={(e) => setS("business.trade_name", e.target.value)} /></div>
              <div><label className={labelCls}>IEC</label><input className={inputCls} data-testid="admin-field-iec" value={settings.business.iec || ""} onChange={(e) => setS("business.iec", e.target.value)} /></div>
              <div><label className={labelCls}>GSTIN</label><input className={inputCls} data-testid="admin-field-gstin" value={settings.business.gstin || ""} onChange={(e) => setS("business.gstin", e.target.value)} /></div>
              <div><label className={labelCls}>Location</label><input className={inputCls} data-testid="admin-field-location" value={settings.business.location || ""} onChange={(e) => setS("business.location", e.target.value)} /></div>
              <div><label className={labelCls}>Operations</label><input className={inputCls} data-testid="admin-field-operations" value={settings.business.operations || ""} onChange={(e) => setS("business.operations", e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Registered Office Address</label><textarea rows={2} className={inputCls} data-testid="admin-field-address" value={settings.business.address} onChange={(e) => setS("business.address", e.target.value)} /></div>
              <div><label className={labelCls}>Business Email</label><input className={inputCls} data-testid="admin-field-email" value={settings.business.email} onChange={(e) => setS("business.email", e.target.value)} /></div>
              <div><label className={labelCls}>Tagline</label><input className={inputCls} data-testid="admin-field-tagline" value={settings.business.tagline} onChange={(e) => setS("business.tagline", e.target.value)} /></div>
              {settings.business.phones.map((p, i) => (
                <div key={i} className="grid grid-cols-2 gap-3 sm:col-span-2 border border-forest/10 bg-white p-4">
                  <div><label className={labelCls}>Phone {i + 1} (display)</label><input className={inputCls} value={p.display} onChange={(e) => setS(`business.phones.${i}.display`, e.target.value)} /></div>
                  <div><label className={labelCls}>Phone {i + 1} (tel: link)</label><input className={inputCls} value={p.tel} onChange={(e) => setS(`business.phones.${i}.tel`, e.target.value)} /></div>
                </div>
              ))}
              {settings.business.whatsapp_numbers.map((w, i) => (
                <div key={i} className="grid grid-cols-2 gap-3 sm:col-span-2 border border-forest/10 bg-white p-4">
                  <div><label className={labelCls}>WhatsApp {i + 1} (display)</label><input className={inputCls} value={w.display} onChange={(e) => setS(`business.whatsapp_numbers.${i}.display`, e.target.value)} /></div>
                  <div><label className={labelCls}>WhatsApp {i + 1} (number, intl format)</label><input className={inputCls} value={w.number} onChange={(e) => setS(`business.whatsapp_numbers.${i}.number`, e.target.value)} /></div>
                </div>
              ))}
              <div>
                <label className={labelCls}>Default WhatsApp Number</label>
                <select className={inputCls} data-testid="admin-field-default-whatsapp" value={settings.business.default_whatsapp} onChange={(e) => setS("business.default_whatsapp", e.target.value)}>
                  {settings.business.whatsapp_numbers.map((w) => <option key={w.number} value={w.number}>{w.display}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2"><label className={labelCls}>WhatsApp Pre-filled Message</label><textarea rows={2} className={inputCls} data-testid="admin-field-whatsapp-message" value={settings.business.whatsapp_message} onChange={(e) => setS("business.whatsapp_message", e.target.value)} /></div>
              {["instagram", "facebook", "linkedin", "youtube"].map((k) => (
                <div key={k}><label className={labelCls}>{k.charAt(0).toUpperCase() + k.slice(1)} URL</label><input className={inputCls} data-testid={`admin-field-social-${k}`} value={settings.business.social[k] || ""} onChange={(e) => setS(`business.social.${k}`, e.target.value)} placeholder="https://..." /></div>
              ))}
            </div>
            <button onClick={() => saveSettings(["business"])} disabled={saving} data-testid="admin-save-business" className="bg-forest px-7 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest-leaf transition-colors disabled:opacity-60">
              {saving ? "Saving..." : "Save Business Info"}
            </button>
          </div>
        )}

        {tab === "Website Content" && settings && (
          <div className="max-w-3xl space-y-10" data-testid="admin-website-content">
            <div>
              <h3 className="font-serif text-2xl text-forest">Homepage Hero</h3>
              <div className="mt-4 space-y-4">
                <div><label className={labelCls}>Subheadline</label><textarea rows={2} className={inputCls} data-testid="admin-field-hero-sub" value={settings.hero.subheadline} onChange={(e) => setS("hero.subheadline", e.target.value)} /></div>
                <SingleImageField label="Hero Background Photo" testId="admin-hero-image" value={settings.hero.image} onChange={(v) => setS("hero.image", v)} />
                <button onClick={() => saveSettings(["hero"])} disabled={saving} data-testid="admin-save-hero" className="bg-forest px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest-leaf transition-colors disabled:opacity-60">Save Hero</button>
              </div>
            </div>

            <div>
              <h3 className="font-serif text-2xl text-forest">Trust Statistics</h3>
              <p className="mt-1 text-xs text-forest/50">These appear on the homepage hero. Keep them factual — e.g. "2025 / Established".</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                {settings.stats.map((st, i) => (
                  <div key={i} className="grid grid-cols-3 gap-3 border border-forest/10 bg-white p-4">
                    <div><label className={labelCls}>Value</label><input className={inputCls} data-testid={`admin-stat-value-${i}`} value={st.value} onChange={(e) => setS(`stats.${i}.value`, e.target.value)} /></div>
                    <div className="col-span-2"><label className={labelCls}>Label</label><input className={inputCls} data-testid={`admin-stat-label-${i}`} value={st.label} onChange={(e) => setS(`stats.${i}.label`, e.target.value)} /></div>
                  </div>
                ))}
              </div>
              <button onClick={() => saveSettings(["stats"])} disabled={saving} data-testid="admin-save-stats" className="mt-4 bg-forest px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest-leaf transition-colors disabled:opacity-60">Save Statistics</button>
            </div>

            <FounderSection settings={settings} setS={setS} saveSettings={saveSettings} saving={saving} />
            <CategoryBannersSection settings={settings} setS={setS} saveSettings={saveSettings} saving={saving} />
            <BrochureSection settings={settings} setS={setS} saveSettings={saveSettings} saving={saving} />
            <CertificationsSection settings={settings} setS={setS} saveSettings={saveSettings} saving={saving} />

            <div>
              <h3 className="font-serif text-2xl text-forest">Export Markets</h3>
              <div className="mt-4 space-y-4">
                <div><label className={labelCls}>Headline</label><input className={inputCls} value={settings.markets.headline} onChange={(e) => setS("markets.headline", e.target.value)} /></div>
                <div><label className={labelCls}>Description</label><textarea rows={2} className={inputCls} value={settings.markets.description} onChange={(e) => setS("markets.description", e.target.value)} /></div>
                <div><label className={labelCls}>Regions (comma separated)</label><input className={inputCls} data-testid="admin-field-regions" value={settings.markets.regions.join(", ")} onChange={(e) => setS("markets.regions", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></div>
                <button onClick={() => saveSettings(["markets"])} disabled={saving} data-testid="admin-save-markets" className="bg-forest px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest-leaf transition-colors disabled:opacity-60">Save Markets</button>
              </div>
            </div>

            <div>
              <h3 className="font-serif text-2xl text-forest">Private Label Section</h3>
              <div className="mt-4 space-y-4">
                <label className="flex items-center gap-3 text-sm text-forest cursor-pointer">
                  <input type="checkbox" checked={settings.private_label.enabled} onChange={(e) => setS("private_label.enabled", e.target.checked)} data-testid="admin-field-private-label-toggle" className="h-4 w-4 accent-[#C69A43]" />
                  Show the Private Label / Custom Packaging section on the homepage
                </label>
                <div><label className={labelCls}>Title</label><input className={inputCls} value={settings.private_label.title} onChange={(e) => setS("private_label.title", e.target.value)} /></div>
                <div><label className={labelCls}>Description</label><textarea rows={2} className={inputCls} value={settings.private_label.description} onChange={(e) => setS("private_label.description", e.target.value)} /></div>
                <button onClick={() => saveSettings(["private_label"])} disabled={saving} data-testid="admin-save-private-label" className="bg-forest px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest-leaf transition-colors disabled:opacity-60">Save Private Label</button>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-2xl text-forest">FAQs</h3>
                <button onClick={() => setS("faqs", [...settings.faqs, { q: "", a: "" }])} data-testid="admin-add-faq" className="inline-flex items-center gap-1.5 border border-forest/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest hover:border-gold transition-colors"><Plus className="h-3.5 w-3.5" /> Add FAQ</button>
              </div>
              <div className="mt-4 space-y-4">
                {settings.faqs.map((f, i) => (
                  <div key={i} className="relative border border-forest/10 bg-white p-4 space-y-3">
                    <button onClick={() => setS("faqs", settings.faqs.filter((_, j) => j !== i))} data-testid={`admin-remove-faq-${i}`} className="absolute right-3 top-3 text-forest/40 hover:text-terracotta transition-colors" aria-label="Remove FAQ"><X className="h-4 w-4" /></button>
                    <div><label className={labelCls}>Question {i + 1}</label><input className={inputCls} data-testid={`admin-faq-q-${i}`} value={f.q} onChange={(e) => setS(`faqs.${i}.q`, e.target.value)} /></div>
                    <div><label className={labelCls}>Answer</label><textarea rows={3} className={inputCls} data-testid={`admin-faq-a-${i}`} value={f.a} onChange={(e) => setS(`faqs.${i}.a`, e.target.value)} /></div>
                  </div>
                ))}
              </div>
              <button onClick={() => saveSettings(["faqs"])} disabled={saving} data-testid="admin-save-faqs" className="mt-4 bg-forest px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest-leaf transition-colors disabled:opacity-60">Save FAQs</button>
            </div>
          </div>
        )}

        {tab === "SEO" && settings && (
          <div className="max-w-3xl" data-testid="admin-seo">
            <p className="text-sm text-[#4A554D]">Edit page titles and meta descriptions. Product pages use their own SEO fields. Canonical URLs always point to <span className="font-mono text-xs">https://www.anvibesexports.com</span>.</p>
            <div className="mt-6 space-y-5">
              <SingleImageField label="Open Graph / Share Image" testId="admin-og-image" value={settings.seo.og_image} onChange={(v) => setS("seo.og_image", v)} />
              {Object.entries(settings.seo.pages).map(([key, page]) => (
                <div key={key} className="border border-forest/10 bg-white p-4 space-y-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-dark">/{key === "home" ? "" : key}</p>
                  <div><label className={labelCls}>Title Tag</label><input className={inputCls} data-testid={`admin-seo-title-${key}`} value={page.title} onChange={(e) => setS(`seo.pages.${key}.title`, e.target.value)} /></div>
                  <div><label className={labelCls}>Meta Description</label><textarea rows={2} className={inputCls} data-testid={`admin-seo-desc-${key}`} value={page.description} onChange={(e) => setS(`seo.pages.${key}.description`, e.target.value)} /></div>
                </div>
              ))}
            </div>
            <button onClick={() => saveSettings(["seo"])} disabled={saving} data-testid="admin-save-seo" className="mt-6 bg-forest px-7 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest-leaf transition-colors disabled:opacity-60">
              {saving ? "Saving..." : "Save SEO Settings"}
            </button>
          </div>
        )}
      </div>

      {editor && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-forest-deep/70 backdrop-blur-sm p-4 sm:p-8" data-testid="product-editor-modal">
          <div className="mx-auto max-w-3xl border border-forest/10 bg-parchment p-6 sm:p-10">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl text-forest">{editor.id ? "Edit Product" : "Add Product"}</h3>
              <button onClick={() => setEditor(null)} data-testid="product-editor-close" className="text-forest/50 hover:text-forest" aria-label="Close editor"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Product Name *</label><input className={inputCls} data-testid="editor-field-name" value={editor.form.name} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, name: e.target.value } })} /></div>
              <div>
                <label className={labelCls}>Category *</label>
                <select className={inputCls} data-testid="editor-field-category" value={editor.form.category} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, category: e.target.value } })}>
                  <option value="rice">Rice</option><option value="spices">Spices</option><option value="tea">Tea</option>
                </select>
              </div>
              <div><label className={labelCls}>URL Slug (blank = auto)</label><input className={inputCls} data-testid="editor-field-slug" value={editor.form.slug} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, slug: e.target.value } })} placeholder="basmati-rice" /></div>
              <div><label className={labelCls}>Origin</label><input className={inputCls} value={editor.form.origin} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, origin: e.target.value } })} /></div>
              <div><label className={labelCls}>Grade</label><input className={inputCls} value={editor.form.grade} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, grade: e.target.value } })} /></div>
              <div><label className={labelCls}>MOQ</label><input className={inputCls} value={editor.form.moq} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, moq: e.target.value } })} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Supply Capacity</label><input className={inputCls} value={editor.form.supply_capacity} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, supply_capacity: e.target.value } })} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Short Description</label><textarea rows={2} className={inputCls} data-testid="editor-field-short-description" value={editor.form.short_description} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, short_description: e.target.value } })} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Detailed Description</label><textarea rows={4} className={inputCls} value={editor.form.description} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, description: e.target.value } })} /></div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Product Photos (first image is the cover)</label>
                <ImageUploader testId="image" images={editor.form.images} onChange={(imgs) => setEditor({ ...editor, form: { ...editor.form, images: imgs } })} />
              </div>
              <div><label className={labelCls}>Available Forms (comma separated)</label><input className={inputCls} value={editor.form.forms} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, forms: e.target.value } })} placeholder="Whole, Powder" /></div>
              <div><label className={labelCls}>Packaging (comma separated)</label><input className={inputCls} value={editor.form.packaging} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, packaging: e.target.value } })} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Certifications (comma separated — only confirmed ones)</label><input className={inputCls} value={editor.form.certifications} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, certifications: e.target.value } })} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Specifications (one per line: Label: Value)</label><textarea rows={6} className={inputCls} data-testid="editor-field-specs" value={editor.form.specs} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, specs: e.target.value } })} placeholder={"Grain Length: 8.35 mm\nMoisture: 12.5% max"} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Shipping Information</label><textarea rows={2} className={inputCls} value={editor.form.shipping_info} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, shipping_info: e.target.value } })} /></div>
              <div><label className={labelCls}>SEO Title</label><input className={inputCls} value={editor.form.seo_title} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, seo_title: e.target.value } })} /></div>
              <div><label className={labelCls}>SEO Description</label><input className={inputCls} value={editor.form.seo_description} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, seo_description: e.target.value } })} /></div>
              <div>
                <label className={labelCls}>Status</label>
                <select className={inputCls} data-testid="editor-field-status" value={editor.form.status} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, status: e.target.value } })}>
                  <option value="active">Active (visible)</option><option value="draft">Draft (hidden)</option>
                </select>
              </div>
              <label className="flex items-center gap-3 text-sm text-forest cursor-pointer self-end pb-2">
                <input type="checkbox" checked={editor.form.featured} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, featured: e.target.checked } })} data-testid="editor-field-featured" className="h-4 w-4 accent-[#C69A43]" />
                Featured on homepage
              </label>
            </div>
            <div className="mt-8 flex gap-3">
              <button onClick={saveProduct} disabled={saving || !editor.form.name} data-testid="product-editor-save" className="bg-forest px-7 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-parchment hover:bg-forest-leaf transition-colors disabled:opacity-60">
                {saving ? "Saving..." : editor.id ? "Save Changes" : "Create Product"}
              </button>
              <button onClick={() => setEditor(null)} className="border border-forest/20 px-7 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-forest hover:border-gold transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
